import { useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { X, Mail, Phone, StickyNote, CalendarCheck, Check } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { key: "email", labelKey: "fuMethodEmail", Icon: Mail },
  { key: "phone", labelKey: "fuMethodPhone", Icon: Phone },
  { key: "note", labelKey: "fuMethodNote", Icon: StickyNote },
];

const methodMeta = (key) => METHODS.find((m) => m.key === key) || METHODS[2];

export default function FollowUpDialog({ job, onClose, onSaved }) {
  const { t, lang } = useT();
  const [method, setMethod] = useState("email");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(() =>
    [...(job.followup_log || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""))
  );

  if (!job) return null;

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "en" ? "en-GB" : "nl-NL", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/followup`, { method, note });
      setLog((cur) => [data.entry, ...cur]);
      setNote("");
      toast.success(t("fuSaved"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("detailUpdateFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 lg:left-72 z-50" data-testid="followup-dialog">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(34rem,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto bg-[#F9F8F6] border border-neutral-300 shadow-2xl">
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-neutral-200 px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <p className="ed-eyebrow flex items-center gap-2">
              <CalendarCheck size={12} strokeWidth={1.5} /> {t("fuTitle")}
            </p>
            <p className="font-serif text-xl mt-1 leading-tight truncate">
              {job.job_title} <span className="italic text-neutral-500">· {job.company}</span>
            </p>
          </div>
          <button
            data-testid="followup-close"
            onClick={onClose}
            className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors shrink-0"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* New follow-up entry */}
          <div>
            <p className="ed-label mb-2">{t("fuHow")}</p>
            <div className="flex flex-wrap gap-2">
              {METHODS.map(({ key, labelKey, Icon }) => (
                <button
                  key={key}
                  data-testid={`followup-method-${key}`}
                  onClick={() => setMethod(key)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${
                    method === key
                      ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                      : "border-neutral-300 hover:border-neutral-900"
                  }`}
                >
                  <Icon size={12} strokeWidth={1.5} /> {t(labelKey)}
                </button>
              ))}
            </div>
            <textarea
              data-testid="followup-note"
              rows={3}
              className="ed-input with-bg mt-3"
              placeholder={t("fuNotePh")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <p className="text-[0.65rem] font-mono text-neutral-500 mt-2">{t("fuResetHint")}</p>
            <button data-testid="followup-save" className="ed-btn mt-3" onClick={save} disabled={busy}>
              <Check size={14} strokeWidth={1.5} /> {busy ? t("saving") : t("fuSave")}
            </button>
          </div>

          {/* Date log */}
          <div>
            <p className="ed-label mb-2">{t("fuHistory")}</p>
            {log.length === 0 ? (
              <p className="text-sm text-neutral-500 italic font-serif" data-testid="followup-empty">{t("fuNoHistory")}</p>
            ) : (
              <ol className="space-y-2" data-testid="followup-log">
                {log.map((entry) => {
                  const { labelKey, Icon } = methodMeta(entry.method);
                  return (
                    <li key={entry.id} className="ed-card p-3 flex items-start gap-3">
                      <span className="mt-0.5 w-7 h-7 rounded-full border border-neutral-300 bg-white flex items-center justify-center shrink-0">
                        <Icon size={13} strokeWidth={1.5} />
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] rounded-full border border-neutral-900 px-2 py-0.5">
                            {t(labelKey)}{entry.auto ? ` · ${t("fuAuto")}` : ""}
                          </span>
                          <span className="font-mono text-[0.65rem] text-neutral-500">{fmt(entry.at)}</span>
                        </span>
                        {entry.note && (
                          <p className="font-serif text-sm text-neutral-700 mt-1 whitespace-pre-wrap">{entry.note}</p>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
